// Lien de proposition : un ou plusieurs créneaux deviennent une adresse que le client ouvre dans son fuseau.
import { label, placeOf } from '../core/contact';
import { proposalText } from '../core/planner';
import { proposalLink, proposalPayload } from '../core/proposal';
import { meetingFits, type Slot } from '../core/schedule';
import { MIN, prettyDate, time } from '../core/time';
import type { Store } from '../data/store';
import { el, esc } from './dom';
import { icon } from './icons';
import { showToast } from './toast';

let dialog: HTMLDialogElement | null = null;

function ensureDialog(): HTMLDialogElement {
  if (dialog && document.body.contains(dialog)) return dialog;
  dialog = document.createElement('dialog');
  dialog.id = 'linkDialog';
  dialog.setAttribute('aria-labelledby', 'linkTitle');
  dialog.innerHTML = `<form method="dialog" novalidate>
    <header class="dlg-head"><div><h2 id="linkTitle">Proposer ce créneau</h2><p>Votre client verra l’heure dans son propre fuseau, en français ou en anglais, et pourra confirmer en un clic.</p></div>
      <button class="icon-btn" type="button" data-close aria-label="Fermer">${icon('close')}</button></header>
    <div class="dlg-body">
      <div class="field" data-duration-field><label for="link-duration">Durée de l’appel</label><select id="link-duration"></select><small class="fit-warn" data-fit></small></div>
      <div class="field"><label for="link-url">Lien à envoyer</label><div class="linkrow"><input type="text" id="link-url" readonly spellcheck="false"><button class="btn primary" type="button" data-copy>Copier</button></div><small class="hint" data-hint></small></div>
      <div class="link-preview" data-preview></div>
      <div class="card-actions"><a class="btn" data-open href="#" target="_blank" rel="noopener">${icon('globe')}Voir comme votre client</a><button class="btn" type="button" data-copy-text>${icon('copy')}Copier en texte</button></div>
      <p class="hint">Le lien porte toutes les informations : rien n’est enregistré sur un serveur. Votre nom et votre e-mail de réception viennent de vos préférences.</p>
    </div>
    <footer class="dlg-foot"><button class="btn" type="button" data-close>Fermer</button></footer></form>`;
  document.body.append(dialog);
  return dialog;
}

async function copy(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}

const DURATIONS: Array<[number, string]> = [[15, '15 minutes'], [30, '30 minutes'], [45, '45 minutes'], [60, '1 heure'], [90, '1 h 30'], [120, '2 heures']];

export function openLink(store: Store, slots: Slot[], meet?: string): void {
  if (!slots.length) return;
  const d = ensureDialog();
  const me = store.state.profile;
  const urlEl = el<HTMLInputElement>(d, '#link-url');
  const hint = el(d, '[data-hint]');
  const durationEl = el<HTMLSelectElement>(d, '#link-duration');
  const baseMin = Math.max(5, Math.round((slots[0]!.end - slots[0]!.start) / MIN));
  const choices = DURATIONS.some(([m]) => m === baseMin) ? DURATIONS : [[baseMin, `${baseMin} minutes`] as [number, string], ...DURATIONS];
  durationEl.innerHTML = choices.map(([m, t]) => `<option value="${m}">${esc(t)}</option>`).join('');
  durationEl.value = String(baseMin);
  // L’événement est déjà posé dans Google Agenda à cette heure-là : changer la durée ici le désynchroniserait.
  el(d, '[data-duration-field]').hidden = !!meet;

  let url = '';
  let current: Slot[] = slots;

  function render(): void {
    const min = Number(durationEl.value);
    const dur = choices.find(([m]) => m === min)?.[1] ?? `${min} minutes`;
    current = slots.map((s) => ({ ...s, end: s.start + min * MIN }));
    url = proposalLink(location.origin + '/p/', proposalPayload(current, me, meet));
    urlEl.value = url;
    el<HTMLAnchorElement>(d, '[data-open]').href = url;
    el(d, '#linkTitle').textContent = current.length > 1 ? `Proposer ${current.length} créneaux` : 'Proposer ce créneau';
    el(d, '[data-preview]').innerHTML = current.map((s) => `<div class="lp-row"><b>${esc(prettyDate(s.start, me.tz))}</b><span class="num">${esc(time(me.tz, s.start))} – ${esc(time(me.tz, s.end))}</span><small>${esc(placeOf(me))} · ${esc(s.people.map((c) => label(c)).join(', '))}</small></div>`).join('')
      + (meet ? `<div class="lp-row"><b>Visio</b><span><a href="${esc(meet)}" target="_blank" rel="noopener">${esc(meet)}</a></span><small>Google Meet, sur la page du client</small></div>` : '');
    // Une durée plus longue peut déborder des horaires déclarés : le dire plutôt que d’envoyer un créneau bancal.
    const over = new Set<string>();
    let overMe = false;
    for (const s of current) {
      if (!meetingFits(me, s.start, min)) overMe = true;
      for (const p of s.people) if (!meetingFits(p, s.start, min)) over.add(label(p));
    }
    const parts = [...(over.size ? [`les horaires de ${[...over].join(', ')}`] : []), ...(overMe ? ['les vôtres'] : [])];
    el(d, '[data-fit]').textContent = parts.length ? `Attention : un appel de ${dur} dépasse ${parts.join(' et ')}.` : '';
  }

  async function copyLink(): Promise<void> {
    const ok = await copy(url);
    el(d, '[data-copy]').textContent = ok ? 'Copié ✓' : 'Copier';
    if (!me.displayName) return; // le conseil sur le nom manquant reste prioritaire
    hint.textContent = ok ? 'Le lien est copié. Collez-le dans votre message.' : 'Sélectionnez le lien et copiez-le avec Ctrl+C.';
  }

  render();
  el(d, '[data-copy]').textContent = 'Copier';
  hint.textContent = me.displayName ? '' : 'Astuce : renseignez votre nom dans Mes préférences pour qu’il apparaisse sur la page.';
  durationEl.onchange = () => { render(); void copyLink(); };

  d.querySelector('form')!.onclick = async (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-close]')) { d.close(); return; }
    if (t.closest('[data-copy]')) { urlEl.select(); await copyLink(); return; }
    if (t.closest('[data-copy-text]')) {
      const text = (current.length === 1 ? proposalText(current[0]!, me) : current.map((s) => proposalText(s, me)).join('\n\n— — —\n\n')) + (meet ? `\n\nVisio : ${meet}` : '');
      const ok = await copy(text);
      showToast({ id: 0, kind: ok ? 'info' : 'error', text: ok ? 'Proposition copiée en texte, prête à coller.' : 'Copie impossible ici : utilisez le lien.' });
    }
  };
  d.querySelector('form')!.onsubmit = (e) => e.preventDefault();
  d.showModal();
  void copyLink();
}
